import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import Grid from "@mui/material/Unstable_Grid2";
import LoadingButton from "@mui/lab/LoadingButton";
import { Auth } from "aws-amplify";

import { AuthWrapper } from "../Pages/AuthWrapper";
import {
  verifyInviteLink,
  InviteApiError,
  completeInvite,
  VerifyInviteResponse,
  completeOnboarding,
  ssoSignInUrlFrom
} from "./inviteUtils";
import { InvitationFormErrors } from "./userInvitationUtils";
import { Regex } from "../../app/constants";
import { EULA } from "./EULA";
import { handleForgotPassword, useForgotPasswordRedirect } from "../login/forgotPasswordUtils";

type ScreenState = "loading" | "error" | "ready";

type SetPasswordForm = {
  password: string;
  confirmPassword: string;
};

/**
 * Extracts token from af_dp parameter as a last resort.
 * af_dp contains a URL-encoded deep link like:
 * simpl://click?deep_link_value=complete-registration&deep_link_sub1=<JWT>
 */
const extractTokenFromAfDp = (afDpEncoded: string): string | null => {
  try {
    const decoded = decodeURIComponent(afDpEncoded);
    // Expected format: "simpl://click?deep_link_value=complete-registration&deep_link_sub1=XYZ"
    const qIndex = decoded.indexOf("?");
    if (qIndex === -1) {
      return null;
    }
    const qp = new URLSearchParams(decoded.slice(qIndex + 1));
    return qp.get("deep_link_sub1");
  } catch (error) {
    console.error("[CompleteRegistration] Error extracting token from af_dp:", error);
    return null;
  }
};

/**
 * Defensively extracts registration token from multiple possible query param shapes.
 * Priority: token (preferred) -> deep_link_sub1 (OneLink fallback) -> af_dp parsing (last resort)
 */
const extractRegistrationToken = (searchParams: URLSearchParams): string | null => {
  // Priority 1: token param (preferred, used by direct links and af_web_dp)
  const token = searchParams.get("token");
  if (token) {
    return token;
  }

  // Priority 2: deep_link_sub1 (OneLink authoritative channel)
  const deepLinkSub1 = searchParams.get("deep_link_sub1");
  if (deepLinkSub1) {
    return deepLinkSub1;
  }

  // Priority 3: Extract from af_dp as last resort
  const afDp = searchParams.get("af_dp");
  if (afDp) {
    const extractedToken = extractTokenFromAfDp(afDp);
    if (extractedToken) {
      return extractedToken;
    }
  }

  return null;
};

/**
 * Checks if the current request is for registration completion.
 * Returns true if deep_link_value === "complete-registration" OR path is /complete-registration
 */
const isCompleteRegistrationIntent = (
  pathname: string,
  searchParams: URLSearchParams
): boolean => {
  const deepLinkValue = searchParams.get("deep_link_value");
  return deepLinkValue === "complete-registration" || pathname === "/complete-registration";
};

export const CompleteRegistration = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [token] = useState(() => {
    const extractedToken = extractRegistrationToken(searchParams);
    return extractedToken || "";
  });

  const [screenState, setScreenState] = useState<ScreenState>(
    token ? "loading" : "error"
  );
  const [errorMessage, setErrorMessage] = useState<string>(
    token ? "" : "This invitation link is missing the token parameter."
  );
  const [verification, setVerification] = useState<VerifyInviteResponse | null>(
    null
  );
  const [formError, setFormError] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [errorMaskedEmail, setErrorMaskedEmail] = useState<string | null>(null);
  const [showEula, setShowEula] = useState(false);
  const [isAcceptingEula, setIsAcceptingEula] = useState(false);
  const [eulaError, setEulaError] = useState<string | null>(null);
  const [userPassword, setUserPassword] = useState<string>(""); // Store password temporarily for re-sign-in if needed
  const [isSigningIn, setIsSigningIn] = useState(false); // Track sign-in process after EULA
  const redirectToForgotPassword = useForgotPasswordRedirect(navigate);
  
  // Use centralized forgot password - if we have email, call directly; otherwise redirect to sign-in
  const handleForgotPasswordClick = async () => {
    if (verification?.email) {
      // We have the email, call forgot password directly using centralized utility
      try {
        await handleForgotPassword(verification.email);
        // Navigate to sign-in page where Amplify will show the reset password form
        navigate("/");
      } catch (error) {
        console.error("Failed to initiate forgot password:", error);
        // On error, still navigate to sign-in page - user can try again there
        navigate("/");
      }
    } else {
      // No email available, use redirect approach
      redirectToForgotPassword();
    }
  };

  // URL normalization: If user landed on / with AppsFlyer params, redirect to canonical URL
  useEffect(() => {
    const isRegistrationIntent = isCompleteRegistrationIntent(
      location.pathname,
      searchParams
    );
    const extractedToken = extractRegistrationToken(searchParams);

    // If this is a registration intent but user is on '/', normalize it
    if (
      isRegistrationIntent &&
      extractedToken &&
      location.pathname !== "/complete-registration"
    ) {
      window.location.replace(
        `/complete-registration?token=${encodeURIComponent(extractedToken)}`
      );
      return;
    }
  }, [location.pathname, searchParams]);

  useEffect(() => {
    if (!token) return;
    try {
      const url = new URL(window.location.href);
      url.searchParams.delete("token");
      url.searchParams.delete("deep_link_value");
      url.searchParams.delete("deep_link_sub1");
      url.searchParams.delete("af_dp");
      url.searchParams.delete("af_ios_url");
      url.searchParams.delete("af_web_dp");
      url.searchParams.delete("pid");
      url.searchParams.delete("af_xp");
      url.searchParams.delete("af_force_deeplink");
      const newUrl =
        url.pathname +
        (url.search ?? "") +
        (url.hash ?? "");
      window.history.replaceState(null, "", newUrl);
    } catch {
      // no-op if URL parsing fails
    }
  }, [token]);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<SetPasswordForm>({
    defaultValues: {
      password: "",
      confirmPassword: ""
    }
  });

  useEffect(() => {
    // Verify this is a registration completion intent
    const isRegistrationIntent = isCompleteRegistrationIntent(
      location.pathname,
      searchParams
    );

    if (!isRegistrationIntent) {
      setErrorStatus(400);
      setScreenState("error");
      setErrorMessage("This link is not a valid registration completion link.");
      setErrorMaskedEmail(null);
      return;
    }

    if (!token) {
      setErrorStatus(400);
      setScreenState("error");
      setErrorMessage("This invitation link is missing the token parameter.");
      setErrorMaskedEmail(null);
      return;
    }

    let isMounted = true;
    setScreenState("loading");
    verifyInviteLink(token)
      .then((result) => {
        if (!isMounted) return;
        setVerification(result);
        setErrorMaskedEmail(null);
        setErrorStatus(null);
        setScreenState("ready");
      })
      .catch((error: unknown) => {
        if (!isMounted) return;
        const ssoSignInUrl = ssoSignInUrlFrom(error);
        if (ssoSignInUrl) {
          window.location.replace(ssoSignInUrl);
          return;
        }
        if (error instanceof InviteApiError) {
          setErrorStatus(error.status);
          setErrorMaskedEmail(error.details?.maskedEmail ?? null);
          setErrorMessage(error.message);
        } else {
          setErrorStatus(null);
          setErrorMaskedEmail(null);
          setErrorMessage(
            "Unable to verify your invitation link right now. Please try again later."
          );
        }
        setScreenState("error");
      });

    return () => {
      isMounted = false;
    };
  }, [token, location.pathname, searchParams]);

  const onSubmit: SubmitHandler<SetPasswordForm> = async (data) => {
    if (!verification) return;
    setFormError("");
    setIsSubmitting(true);
    try {
      await completeInvite({
        redeemToken: verification.redeemToken,
        password: data.password
      });

      // Store password temporarily for potential re-sign-in after EULA
      setUserPassword(data.password);

      // Check if user is already authenticated (completeInvite might have authenticated them)
      let isAuthenticated = false;
      try {
        await Auth.currentAuthenticatedUser();
        isAuthenticated = true;
      } catch (notAuthenticated) {
        // User is not authenticated, wait for Cognito to propagate password, then sign in
        await new Promise((resolve) => setTimeout(resolve, 2000));
        
        try {
          const signInResult = await Auth.signIn(
            verification.email.toLowerCase(),
            data.password
          );
          
          // Handle challenge if present
          if (signInResult.challengeName === "NEW_PASSWORD_REQUIRED") {
            // Complete the new password challenge (result not needed, Auth state is updated)
            await Auth.completeNewPassword(
              signInResult,
              data.password,
              {}
            );
          }
          isAuthenticated = true;
        } catch (signInError) {
          console.error("Sign-in error:", signInError);
          const errorMessage = signInError instanceof Error
            ? signInError.message
            : "Password was set successfully, but automatic sign-in failed. Please try signing in manually.";
          setFormError(errorMessage);
          // Don't show EULA if sign-in failed - completeOnboarding requires authentication
          setShowEula(false);
          return;
        }
      }
      
      // Show EULA once user is authenticated (either already was or just signed in)
      if (isAuthenticated) {
        setShowEula(true);
      }
    } catch (error: unknown) {
      console.error("Error in onSubmit:", error);
      const ssoSignInUrl = ssoSignInUrlFrom(error);
      if (ssoSignInUrl) {
        window.location.replace(ssoSignInUrl);
        return;
      }
      if (error instanceof InviteApiError) {
        if (error.status === 409 || error.status === 410) {
          setErrorMessage(error.message);
          setErrorStatus(error.status);
          setErrorMaskedEmail(error.details?.maskedEmail ?? null);
          setScreenState("error");
        } else {
          setFormError(error.message);
        }
      } else {
        const message =
          error instanceof Error
            ? error.message
            : "Unable to complete registration.";
        setFormError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (screenState === "loading") {
    return (
      <AuthWrapper title="Verifying your invitation">
        <Box display="flex" justifyContent="center" paddingY={6}>
          <CircularProgress />
        </Box>
      </AuthWrapper>
    );
  }

  // Show loading spinner if signing in after EULA acceptance
  if (isSigningIn) {
    return (
      <AuthWrapper title="Signing you in...">
        <Box display="flex" justifyContent="center" paddingY={6}>
          <CircularProgress />
        </Box>
      </AuthWrapper>
    );
  }

  if (screenState === "error") {
    return (
      <AuthWrapper title="Registration link issue">
        <Alert severity="error" sx={{ marginBottom: 2 }}>
          {errorMessage}
          {errorMaskedEmail ? ` (${errorMaskedEmail})` : null}
        </Alert>
        {errorStatus === 409 ? (
          <>
            <Typography sx={{ marginBottom: 2 }}>
              This account already appears to be active. Try signing in, or use
              the password reset link below if you need to set a new password.
            </Typography>
            <Stack direction="row" spacing={2}>
              <LoadingButton variant="contained" onClick={() => navigate("/")}>
                Go to Sign In
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                onClick={handleForgotPasswordClick}
              >
                Forgot Password?
              </LoadingButton>
            </Stack>
          </>
        ) : (
          <Typography>
            Need a new invitation? Contact{" "}
            <Link href="mailto:help@simpl.org">help@simpl.org</Link>.
          </Typography>
        )}
      </AuthWrapper>
    );
  }

  return (
    <>
      <AuthWrapper
      title="Complete your registration"
      subtitle={
        verification ? `Invitation for ${verification.maskedEmail}` : undefined
      }
    >
      <Grid xs={12}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Grid container spacing={2}>
            <Grid xs={12}>
              <TextField
                label="Email"
                value={verification?.maskedEmail || ""}
                disabled
                sx={{ width: "100%" }}
              />
            </Grid>
            <Grid xs={12}>
              <Controller
                control={control}
                name="password"
                rules={{
                  required: InvitationFormErrors.NoPassword,
                  pattern: {
                    value: Regex.Password,
                    message: InvitationFormErrors.PasswordRequirementsNotMet
                  }
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    autoFocus
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    label="New Password"
                    type="password"
                    sx={{ width: "100%" }}
                    disabled={isSubmitting}
                    required
                  />
                )}
              />
            </Grid>
            {errors.password && (
              <Grid xs={12}>
                <Alert severity="error">
                  {errors.password.message || InvitationFormErrors.NoPassword}
                </Alert>
              </Grid>
            )}
            <Grid xs={12}>
              <Controller
                control={control}
                name="confirmPassword"
                rules={{
                  required: InvitationFormErrors.NoConfirmPassword,
                  validate: (value, formValues) =>
                    value === formValues.password ||
                    InvitationFormErrors.PasswordsDoNotMatch
                }}
                render={({ field: { onChange, onBlur, value } }) => (
                  <TextField
                    onChange={onChange}
                    onBlur={onBlur}
                    value={value}
                    label="Confirm Password"
                    type="password"
                    sx={{ width: "100%" }}
                    disabled={isSubmitting}
                    required
                  />
                )}
              />
            </Grid>
            {errors.confirmPassword && (
              <Grid xs={12}>
                <Alert severity="error">
                  {errors.confirmPassword.message ||
                    InvitationFormErrors.NoConfirmPassword}
                </Alert>
              </Grid>
            )}
            {formError && (
              <Grid xs={12}>
                <Alert severity="error">{formError}</Alert>
              </Grid>
            )}
            <Grid xs={12}>
              <LoadingButton
                loading={isSubmitting}
                disabled={isSubmitting}
                variant="contained"
                type="submit"
                sx={{ width: "100%" }}
              >
                Submit
              </LoadingButton>
            </Grid>
            <Grid xs={12} sx={{ textAlign: "center", paddingTop: 4 }}>
              <Link href="mailto:help@simpl.org?subject=Registration Help">
                Help &amp; Support
              </Link>
            </Grid>
          </Grid>
        </form>
      </Grid>
      </AuthWrapper>
      <EULA
        open={showEula}
        setOpen={setShowEula}
        loading={isAcceptingEula || isSigningIn}
        onAccept={handleEulaAccept}
        allowCancel={false}
        errorMessage={eulaError}
      />
    </>
  );

  async function handleEulaAccept() {
    try {
      setEulaError(null);
      setIsAcceptingEula(true);

      // CRITICAL: Ensure user is authenticated BEFORE calling completeOnboarding()
      // The complete-onboarding lambda requires a valid Cognito JWT token
      let isAuthenticated = false;
      try {
        await Auth.currentAuthenticatedUser();
        isAuthenticated = true;
      } catch (notAuthenticated) {
        // User is not authenticated, sign in first
        if (verification?.email && userPassword) {
          try {
            const signInResult = await Auth.signIn(
              verification.email.toLowerCase(),
              userPassword
            );
            
            // Handle challenge if present
            if (signInResult.challengeName === "NEW_PASSWORD_REQUIRED") {
              await Auth.completeNewPassword(signInResult, userPassword, {});
            }
            
            // Verify sign-in was successful
            await Auth.currentAuthenticatedUser();
            isAuthenticated = true;
          } catch (signInError) {
            setEulaError(
              "Unable to sign in. Please try signing in manually and accept the EULA again."
            );
            setUserPassword("");
            setIsAcceptingEula(false);
            setShowEula(false);
            return;
          }
        } else {
          setEulaError(
            "Unable to sign in. Please try signing in manually and accept the EULA again."
          );
          setUserPassword("");
          setIsAcceptingEula(false);
          setShowEula(false);
          return;
        }
      }

      if (!isAuthenticated) {
        setEulaError(
          "Unable to authenticate. Please try signing in manually and accept the EULA again."
        );
        setUserPassword("");
        setIsAcceptingEula(false);
        setShowEula(false);
        return;
      }

      // Now that user is authenticated, verify we have a valid session token
      try {
        await Auth.currentSession();
      } catch (sessionError) {
        setEulaError(
          "Session token not available. Please try signing in manually and accept the EULA again."
        );
        setUserPassword("");
        setIsAcceptingEula(false);
        setShowEula(false);
        return;
      }

      // Call completeOnboarding now that we're authenticated
      await completeOnboarding();

      // Transition to sign-in loading state (keep EULA modal open with spinner)
      setIsAcceptingEula(false);
      setIsSigningIn(true);

      // Small delay to ensure API call completes
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Clear the stored password for security
      setUserPassword("");
      // Use window.location to force full page reload so Authenticator picks up the session
      window.location.href = "/";
    } catch (error) {
      console.error("[CompleteRegistration] handleEulaAccept: Error in EULA acceptance flow:", error);
      setEulaError(
        error instanceof Error
          ? error.message
          : "Unable to record EULA acceptance."
      );
      // Clear the stored password for security
      setUserPassword("");
      setIsAcceptingEula(false);
      setIsSigningIn(false);
    }
  }
};

